import { useState, useEffect } from 'react';
import { X, User, ChevronDown, Loader2, Clock, CheckCheck } from 'lucide-react';
import api from '../../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENCON_ORANGE = '#ee7203';
const LS_KEY = 'pt_last_owners';
const MAX_LAST = 5;

function getLastOwners() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; }
}
function saveLastOwner(user) {
  const existing = getLastOwners().filter(u => u.id !== user.id);
  localStorage.setItem(LS_KEY, JSON.stringify([user, ...existing].slice(0, MAX_LAST)));
}

/**
 * API error `detail` can be a plain string (our own HTTPException calls) OR
 * a FastAPI/Pydantic validation error, which comes back as an ARRAY of
 * {loc, msg, type} objects. Rendering that array directly as a React child
 * (e.g. `{error}` in JSX) throws "Objects are not valid as a React child"
 * and crashes the whole tree to a white screen — always reduce it to a
 * plain string first.
 */
function extractErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map(d => (typeof d === 'string' ? d : d?.msg || (d ? JSON.stringify(d) : '')))
      .filter(Boolean);
    return msgs.length ? msgs.join('; ') : fallback;
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return fallback;
}

const PRIORITIES = ['Normal', 'Low', 'Medium', 'High'];
const STATUSES = ['Not Started', 'In Progress', 'Completed', 'Deferred'];

/**
 * Unified Add/Edit modal for Notes and Tasks.
 *
 * Props:
 *   type        - 'note' | 'task'
 *   mode        - 'add' | 'edit'
 *   target      - { type, module?, id, name } (for add)
 *   existing    - existing note/task object (for edit mode)
 *   createUrl   - POST endpoint (add mode)
 *   projectId   - optional: if set, store project↔task link after creation
 *   onClose     - fn()
 *   onSaved     - fn()
 */
export default function NoteTaskModal({ type, mode = 'add', target, existing, createUrl, projectId, onClose, onSaved }) {
  // Note fields
  const [content, setContent] = useState(existing?.content || '');

  // Task fields
  const [subject, setSubject] = useState(existing?.subject || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [dueDate, setDueDate] = useState(existing?.due_date ? existing.due_date.slice(0, 10) : '');
  const [priority, setPriority] = useState(existing?.priority || 'Normal');
  const [status, setStatus] = useState(existing?.status || 'Not Started');

  // Owner (tasks only)
  const [ownerQuery, setOwnerQuery] = useState('');
  const [ownerDropOpen, setOwnerDropOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedOwner, setSelectedOwner] = useState(existing?.owner_id ? { id: existing.owner_id, name: existing.owner_name || '' } : null);
  const [lastOwners, setLastOwners] = useState(getLastOwners());

  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');

  const isNote = type === 'note';

  useEffect(() => {
    if (isNote) return; // notes don't need user list
    api.get(`${API}/zoho/crm-users`)
      .then(r => setAllUsers(r.data.users || []))
      .catch(() => setAllUsers([]))
      .finally(() => setUsersLoading(false));
  }, [isNote]);

  const filteredUsers = ownerQuery.trim()
    ? allUsers.filter(u =>
        u.name.toLowerCase().includes(ownerQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(ownerQuery.toLowerCase())
      )
    : allUsers.slice(0, 8);

  const pickOwner = (u) => {
    setSelectedOwner(u);
    setOwnerQuery('');
    setOwnerDropOpen(false);
    saveLastOwner(u);
    setLastOwners(getLastOwners());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (mode === 'edit') {
        if (type === 'note') {
          await api.put(`${API}/zoho/notes/${existing.id}`, {
            note_content: content,
          });
        } else {
          await api.put(`${API}/zoho/tasks/${existing.id}`, {
            subject,
            description,
            due_date: dueDate || null,
            priority,
            status,
            owner_zoho_id: selectedOwner?.id || null,
          });
        }
      } else {
        if (type === 'note') {
          await api.post(createUrl, {
            note_content: content,
          });
        } else {
          const createRes = await api.post(`${API}/zoho/create-task`, {
            record_id: target.id,
            module: target.module,
            subject,
            description,
            due_date: dueDate || null,
            priority,
            status,
            owner_zoho_id: selectedOwner?.id || null,
          });
          // Store project↔task link if we know the project
          if (projectId) {
            const newTaskId = createRes?.data?.task?.id || createRes?.data?.id;
            if (newTaskId) {
              api.post(`${API}/project-task-links`, { task_id: newTaskId, project_id: projectId }).catch(() => {});
            }
          }
        }
      }
      onSaved();
    } catch (err) {
      setError(extractErrorMessage(err, 'Save failed, please try again'));
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!existing?.id) return;
    setCompleting(true);
    setError('');
    try {
      await api.post(`${API}/zoho/tasks/${existing.id}/complete`);
      onSaved();
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not complete task'));
    } finally {
      setCompleting(false);
    }
  };

  const modalTitle = mode === 'edit'
    ? (isNote ? 'Edit Note' : 'Edit Task')
    : (isNote ? 'Add Note' : 'Add Task');
  const isValid = isNote ? content.trim().length > 0 : subject.trim().length > 0;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-3">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col" style={{ maxHeight: 'min(90vh, 640px)' }}>

        {/* ── Fixed header ─────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-sm font-bold text-gray-800">{modalTitle}</h3>
            {(target?.name || existing?.subject) && (
              <p className="text-[11px] text-gray-400 mt-0.5 truncate max-w-[280px]">
                {target?.name || existing?.subject}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Scrollable form body ──────────────────────────────── */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">

            {/* ── Note fields (no owner, no title) ─────────── */}
            {isNote ? (
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Note *</label>
                <textarea rows={5} value={content} onChange={e => setContent(e.target.value)}
                  placeholder="Write your note here…"
                  className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 text-sm outline-none focus:border-orange-400 transition-colors resize-none"
                  autoFocus
                  required />
              </div>
            ) : (
              /* ── Task fields (with owner) ─────────────── */
              <>
                {/* Owner selector */}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Owner</label>

                  {lastOwners.length > 0 && !selectedOwner && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <span className="text-[10px] text-gray-400 self-center flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" /> Recent:
                      </span>
                      {lastOwners.map(u => (
                        <button key={u.id} type="button" onClick={() => pickOwner(u)}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-700 transition-colors">
                          {u.name.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedOwner ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 bg-orange-50" style={{ borderColor: ENCON_ORANGE }}>
                      <User className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ENCON_ORANGE }} />
                      <span className="text-sm font-semibold flex-1 truncate" style={{ color: ENCON_ORANGE }}>{selectedOwner.name}</span>
                      <button type="button" onClick={() => setSelectedOwner(null)} className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-gray-200 focus-within:border-orange-400 bg-white transition-colors">
                        <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <input
                          type="text"
                          placeholder="Search Zoho users…"
                          value={ownerQuery}
                          onChange={e => { setOwnerQuery(e.target.value); setOwnerDropOpen(true); }}
                          onFocus={() => setOwnerDropOpen(true)}
                          className="flex-1 text-sm bg-transparent outline-none min-w-0"
                        />
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      </div>
                      {ownerDropOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOwnerDropOpen(false)} />
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 z-20 max-h-40 overflow-y-auto">
                            {usersLoading ? (
                              <div className="p-3 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                              </div>
                            ) : filteredUsers.length === 0 ? (
                              <div className="p-3 text-center text-gray-400 text-sm">No users found</div>
                            ) : (
                              filteredUsers.map(u => (
                                <button key={u.id} type="button" onClick={() => pickOwner(u)}
                                  className="w-full text-left px-3 py-2 hover:bg-orange-50 transition-colors">
                                  <div className="text-sm font-medium text-gray-800">{u.name}</div>
                                  {u.email && <div className="text-[10px] text-gray-400">{u.email}</div>}
                                </button>
                              ))
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Subject *</label>
                  <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                    placeholder="Task subject…"
                    className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 text-sm outline-none focus:border-orange-400 transition-colors"
                    required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Due Date</label>
                    <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 text-sm outline-none focus:border-orange-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Priority</label>
                    <select value={priority} onChange={e => setPriority(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 text-sm outline-none focus:border-orange-400 transition-colors bg-white">
                      {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 text-sm outline-none focus:border-orange-400 transition-colors bg-white">
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Description</label>
                  <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}
                    placeholder="Optional description…"
                    className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 text-sm outline-none focus:border-orange-400 transition-colors resize-none" />
                </div>
              </>
            )}

            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>

          {/* ── Fixed footer ────────────────────────────────────── */}
          <div className="flex gap-2 px-5 py-3.5 border-t border-gray-100 flex-shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl border-2 border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            {/* Complete & Close — task edit only, not already completed */}
            {!isNote && mode === 'edit' && existing?.status !== 'Completed' && (
              <button type="button" onClick={handleComplete} disabled={completing || saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#16a34a' }}
                data-testid="task-complete-close-btn">
                {completing
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <CheckCheck className="w-3.5 h-3.5" />
                }
                Complete & Close
              </button>
            )}
            <button type="submit" disabled={saving || !isValid || completing}
              className="flex-1 px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
              style={{ backgroundColor: ENCON_ORANGE }}>
              {saving
                ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</span>
                : (mode === 'edit' ? 'Save Changes' : (isNote ? 'Add Note' : 'Add Task'))
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
